import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ParsedUser } from "src/domains/myPageContent/accountInfo/user-query/user.model";
import { useGetUser, usePatchUser } from "src/domains/myPageContent/accountInfo/user-query/user.queries";
import { convertToNumberWithNationCode } from "src/domains/shared/utils/validationUtils";
import { resolveQueryRenderState } from "src/domains/shared/utils/queryRenderState";
import notice from "src/domains/shared/utils/notice";

/** 한 줄씩 고칠 수 있는 계정 항목. 이메일·표시 시간대는 고칠 수 없다. */
export type EditableAccountField = "name" | "phone_number" | "company" | "job" | "field";

export type AccountFieldValidation =
  | { ok: true }
  | { ok: false; reason: "required" | "phone" };

const PHONE_NUMBER_PATTERN = /^\d{10,11}$/;

/** 필드 하나의 입력값을 검증한다 — 모든 편집 항목은 필수, 휴대폰 번호는 숫자 10~11자리. */
export const validateAccountField = (
  field: EditableAccountField,
  draft: string
): AccountFieldValidation => {
  if (!draft.trim()) return { ok: false, reason: "required" };
  if (field === "phone_number" && !PHONE_NUMBER_PATTERN.test(draft)) {
    return { ok: false, reason: "phone" };
  }
  return { ok: true };
};

/**
 * 수정 요청 페이로드 — 기존 계정 정보 수정과 같은 형식(이름은 username 으로,
 * 휴대폰 번호는 국가코드 형식으로)이며 전체 사용자 정보에 바뀐 한 필드만 덮어쓴다.
 */
export const buildAccountPatchPayload = (
  user: ParsedUser,
  field: EditableAccountField,
  draft: string
) => {
  const next: ParsedUser = { ...user, [field]: draft };
  const { name, ...rest } = next;
  return {
    username: name,
    ...rest,
    phone_number: convertToNumberWithNationCode(next.phone_number),
  };
};

export const useAccountInfo = () => {
  const { t } = useTranslation("projects");
  const queryClient = useQueryClient();
  const userQuery = useGetUser();
  const { mutate: patchUser, isLoading: isSaving } = usePatchUser();
  const userInfo = userQuery.data;

  const [editingField, setEditingField] = useState<EditableAccountField | null>(null);
  const [draft, setDraft] = useState("");
  const [validation, setValidation] = useState<AccountFieldValidation>({ ok: true });

  const renderState = resolveQueryRenderState({
    status: userQuery.status,
    fetchStatus: userQuery.fetchStatus,
    itemCount: userInfo ? 1 : 0,
    hasData: userInfo !== undefined,
  });

  const startEdit = (field: EditableAccountField) => {
    if (!userInfo || isSaving) return;
    setEditingField(field);
    setDraft(String(userInfo[field] ?? ""));
    setValidation({ ok: true });
  };

  const cancelEdit = () => {
    if (isSaving) return;
    setEditingField(null);
    setValidation({ ok: true });
  };

  const changeDraft = (value: string) => {
    setDraft(value);
    if (!validation.ok) setValidation({ ok: true });
  };

  const confirmEdit = () => {
    if (!userInfo || !editingField || isSaving) return;
    const result = validateAccountField(editingField, draft);
    if (!result.ok) {
      setValidation(result);
      return;
    }
    patchUser(buildAccountPatchPayload(userInfo, editingField, draft) as ParsedUser, {
      onSuccess: () => {
        void queryClient.refetchQueries(["getUser"]);
        notice("success", t("myPage.messages.accountUpdated", { ns: "projects" }));
        setEditingField(null);
      },
      onError: () => {
        notice("error", t("myPage.messages.accountUpdateFailed", { ns: "projects" }));
        setEditingField(null);
      },
    });
  };

  return {
    userInfo,
    renderState,
    refetchUser: () => void userQuery.refetch(),
    editingField,
    draft,
    validation,
    isSaving,
    startEdit,
    cancelEdit,
    changeDraft,
    confirmEdit,
  };
};
